import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useScriptLoader } from '../contexts/ScriptLoaderContext';
import MonacoEditor from './MonacoEditor';
import NavBar from './NavBar';
import '../styles/CourseTemplate.css';

const CourseTemplate = () => {
    const { courseId, stepId } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { scriptsReady } = useScriptLoader();

    // State management
    const [courseData, setCourseData] = useState(null);
    const [stepData, setStepData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [code, setCode] = useState('');
    const [output, setOutput] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const [checkPoints, setCheckPoints] = useState([]);
    const [showHint, setShowHint] = useState(false);
    const [hintIndex, setHintIndex] = useState(0);
    const [pyodideReady, setPyodideReady] = useState(false);
    const [attemptCount, setAttemptCount] = useState(0);
    const [showSolution, setShowSolution] = useState(false);
    const [stepCompleted, setStepCompleted] = useState(false);

    const pyodideRef = useRef(null);
    const iframeRef = useRef(null);
    const monacoEditorRef = useRef(null);

    // Initialize Pyodide for Python execution
    useEffect(() => {
        const initPyodide = async () => {
            try {
                if (window.pyodide) {
                    pyodideRef.current = window.pyodide;
                    setPyodideReady(true);
                    return;
                }

                const checkPyodide = setInterval(() => {
                    if (window.pyodide) {
                        pyodideRef.current = window.pyodide;
                        setPyodideReady(true);
                        clearInterval(checkPyodide);
                    }
                }, 100);

                setTimeout(() => {
                    clearInterval(checkPyodide);
                    if (!window.pyodide) {
                        console.error('Pyodide loading timeout');
                    }
                }, 10000);
            } catch (error) {
                console.error('Failed to initialize Pyodide:', error);
            }
        };

        initPyodide();
    }, []);

    // Load course metadata
    useEffect(() => {
        const loadCourseData = async () => {
            try {
                const response = await fetch(`/courses/${courseId}/meta.json`);
                if (response.ok) {
                    const data = await response.json();
                    setCourseData(data);
                }
            } catch (error) {
                console.error('Failed to load course metadata:', error);
            }
        };

        loadCourseData();
    }, [courseId]);

    // Load step data and initialize state
    useEffect(() => {
        const loadStepData = async () => {
            try {
                setLoading(true);
                
                // Reset state for new step
                setCode('');
                setOutput('');
                setCheckPoints([]);
                setShowHint(false);
                setHintIndex(0);
                setAttemptCount(0);
                setShowSolution(false);

                // Load article for step 1
                if (stepId === '1') {
                    const response = await fetch(`/courses/${courseId}/article.md`);
                    const content = await response.text();
                    setStepData({
                        stepId: 1,
                        type: 'article',
                        content: content,
                        title: '축구 카드의 세계로!'
                    });
                } else {
                    // Load step JSON file
                    const response = await fetch(`/courses/${courseId}/step${stepId}.json`);
                    if (!response.ok) {
                        throw new Error('Step file not found');
                    }

                    const data = await response.json();
                    setStepData(data);

                    // Initialize checkpoints
                    if (data.content?.ko?.validation?.checkPoints) {
                        const initialCheckPoints = data.content.ko.validation.checkPoints.map(cp => ({
                            ...cp,
                            completed: false
                        }));
                        setCheckPoints(initialCheckPoints);
                    }

                    // Load saved progress
                    const progressKey = `course_${courseId}_step_${stepId}_completed`;
                    const attemptKey = `course_${courseId}_step_${stepId}_attempts`;
                    const codeKey = `course_${courseId}_step_${stepId}_code`;
                    const checkpointsKey = `course_${courseId}_step_${stepId}_checkpoints`;

                    const savedProgress = localStorage.getItem(progressKey);
                    const savedAttempts = localStorage.getItem(attemptKey);
                    const savedCode = localStorage.getItem(codeKey);
                    const savedCheckpoints = localStorage.getItem(checkpointsKey);

                    setStepCompleted(savedProgress === 'true');
                    setAttemptCount(savedAttempts ? parseInt(savedAttempts, 10) : 0);

                    // Restore saved checkpoints if available
                    if (savedCheckpoints && data.content?.ko?.validation?.checkPoints) {
                        try {
                            const checkpointStatus = JSON.parse(savedCheckpoints);
                            const restoredCheckPoints = data.content.ko.validation.checkPoints.map(cp => ({
                                ...cp,
                                completed: checkpointStatus[cp.id] || false
                            }));
                            setCheckPoints(restoredCheckPoints);
                        } catch (e) {
                            console.error('Failed to restore checkpoints:', e);
                        }
                    }

                    // Set initial code
                    if (savedProgress === 'true' && savedCode) {
                        // If completed, show saved successful code
                        setCode(savedCode);
                    } else if (data.editor?.starterCode?.ko) {
                        // Show starter code
                        setCode(data.editor.starterCode.ko);
                    }
                }

            } catch (error) {
                console.error('Failed to load step data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadStepData();
    }, [stepId, courseId]);

    // Get current step type from meta.json
    const getCurrentStepType = () => {
        if (!courseData?.steps) return 'coding-python';
        const currentStep = courseData.steps.find(step => step.id === parseInt(stepId));
        return currentStep?.type || 'coding-python';
    };

    // Get current step language
    const getCurrentStepLanguage = () => {
        if (!courseData?.steps) return 'python';
        const currentStep = courseData.steps.find(step => step.id === parseInt(stepId));
        return currentStep?.language || 'python';
    };

    // Markdown to HTML converter
    const convertMarkdownToHtml = (markdown) => {
        if (!markdown) return '';

        let html = markdown;
        
        // Headers
        html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
        html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
        html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
        
        // Bold and italic
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
        
        // Code blocks
        html = html.replace(/```([^`]+)```/g, '<pre><code>$1</code></pre>');
        html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
        
        // Lists
        html = html.replace(/^\* (.+)$/gim, '<li>$1</li>');
        html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
        
        // Images
        html = html.replace(/!\[([^\]]+)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
        
        // Links
        html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
        
        // Paragraphs
        html = html.replace(/\n\n/g, '</p><p>');
        html = '<p>' + html + '</p>';
        
        // Horizontal rules
        html = html.replace(/^---$/gim, '<hr>');

        return html;
    };

    // Python code execution
    const runPythonCode = async () => {
        if (!pyodideReady || !pyodideRef.current || isRunning) {
            if (!pyodideReady) {
                setOutput('Python 환경을 준비중입니다. 잠시만 기다려주세요...');
            }
            return;
        }

        setIsRunning(true);
        setOutput('');

        try {
            // Setup stdout capture
            pyodideRef.current.runPython(`
import sys
from io import StringIO
sys.stdout = StringIO()
            `);

            // Execute user code
            await pyodideRef.current.runPythonAsync(code);

            // Get output
            const stdout = pyodideRef.current.runPython(`
result = sys.stdout.getvalue()
sys.stdout = sys.__stdout__
result
            `);

            setOutput(stdout);

            // Validate Python code
            await validatePythonCode(stdout);

        } catch (error) {
            let errorMessage = error.message;
            
            // Handle syntax errors with friendly messages
            if (error.message.includes('SyntaxError') || error.message.includes('invalid syntax')) {
                if (code.includes('= \n') || code.includes('= ')) {
                    errorMessage = `아직 값을 입력하지 않았어요! 😊

예시:
• name = "손흥민"  (문자열은 따옴표로 감싸기)
• number = 7      (숫자는 따옴표 없이)
• is_captain = True  (True 또는 False만)

각 변수에 값을 넣어주세요!`;
                } else {
                    errorMessage = `코드에 문법 오류가 있어요. 다시 확인해보세요!
${error.message}`;
                }
            }
            
            setOutput(errorMessage);
        } finally {
            setIsRunning(false);
        }
    };

    // HTML code execution
    const runHtmlCode = () => {
        setIsRunning(true);
        
        try {
            // Create safe HTML document
            const htmlDocument = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            margin: 20px;
            line-height: 1.6;
        }
        .player-card {
            border: 2px solid #ddd;
            border-radius: 8px;
            padding: 20px;
            max-width: 300px;
            margin: 20px auto;
            background: white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .captain { color: gold; }
        .forward { border-color: #ff4444; }
        .midfielder { border-color: #44ff44; }
        .defender { border-color: #4444ff; }
        .goalkeeper { border-color: #ff44ff; }
    </style>
</head>
<body>
    ${code}
</body>
</html>`;

            // Render in iframe
            if (iframeRef.current) {
                iframeRef.current.srcdoc = htmlDocument;
            }

            // Set preview message
            setOutput(`
<div class="html-preview-info">
    <h4>🎨 HTML 미리보기가 생성되었습니다!</h4>
    <p>위의 미리보기 창에서 결과를 확인하세요.</p>
</div>
            `);

            // Validate HTML code
            console.log('About to validate HTML code:', code);
            validateHtmlCode(code);

        } catch (error) {
            setOutput(`HTML 렌더링 오류: ${error.message}`);
        } finally {
            setIsRunning(false);
        }
    };

    // Main code execution function
    const runCode = async () => {
        // Increment attempt count
        const newAttemptCount = attemptCount + 1;
        setAttemptCount(newAttemptCount);
        
        // Save attempt count
        const attemptKey = `course_${courseId}_step_${stepId}_attempts`;
        localStorage.setItem(attemptKey, newAttemptCount.toString());

        // Execute based on step type
        const stepType = getCurrentStepType();
        if (stepType === 'coding-html') {
            runHtmlCode();
        } else {
            await runPythonCode();
        }
    };

    // Python code validation
    const validatePythonCode = async (output) => {
        if (!stepData?.content?.ko?.validation?.checkPoints) return;

        const updatedCheckPoints = [...checkPoints];
        let completedCount = 0;

        try {
            // Create validation code to check variables
            const codeToCheck = `
${code}

# Validation result collection
validation_result = {}

# Check if variables exist and get their types and values
important_vars = ['name', 'number', 'is_captain', 'is_injured', 'can_play', 'has_yellow_card', 'player']

for var in important_vars:
    if var in locals() or var in globals():
        validation_result[f'{var}_exists'] = True
        if var in locals():
            validation_result[f'{var}_value'] = locals()[var]
            validation_result[f'{var}_type'] = type(locals()[var]).__name__
        elif var in globals():
            validation_result[f'{var}_value'] = globals()[var] 
            validation_result[f'{var}_type'] = type(globals()[var]).__name__
    else:
        validation_result[f'{var}_exists'] = False

# Check player dictionary structure if exists
if 'player' in locals() or 'player' in globals():
    player_var = locals().get('player', globals().get('player'))
    if isinstance(player_var, dict):
        validation_result['player_keys'] = list(player_var.keys())
        validation_result['player_values'] = player_var

import json
print("VALIDATION_RESULT:" + json.dumps(validation_result))
            `;

            // Execute validation code
            pyodideRef.current.runPython(`
import sys
from io import StringIO
sys.stdout = StringIO()
            `);

            await pyodideRef.current.runPythonAsync(codeToCheck);

            const validationOutput = pyodideRef.current.runPython(`
result = sys.stdout.getvalue()
sys.stdout = sys.__stdout__
result
            `);

            // Parse validation results
            const resultMatch = validationOutput.match(/VALIDATION_RESULT:(.+)/);
            if (resultMatch) {
                const validationResult = JSON.parse(resultMatch[1]);
                console.log('Validation result:', validationResult);

                // Step-specific validation logic
                completedCount = validateStepSpecific(stepId, validationResult, output, updatedCheckPoints);
            }

        } catch (error) {
            console.error('Validation error:', error);
        }

        setCheckPoints(updatedCheckPoints);
        showValidationFeedback(completedCount, updatedCheckPoints);
    };

    // HTML code validation
    const validateHtmlCode = (htmlCode) => {
        if (!stepData?.content?.ko?.validation?.checkPoints) return;

        const updatedCheckPoints = [...checkPoints];
        let completedCount = 0;

        console.log('Validating HTML code for step:', stepId);
        console.log('HTML code:', htmlCode);
        console.log('Available checkpoints:', updatedCheckPoints.map(cp => cp.id));

        // Step-specific HTML validation
        if (stepId === '6') {
            // Check for card container with player-card class
            if (htmlCode.includes('player-card') || htmlCode.includes('class="player-card"') || htmlCode.includes("class='player-card'")) {
                console.log('✓ Found player-card container');
                updateCheckPoint('has_card_container', true, updatedCheckPoints);
                completedCount++;
            } else {
                console.log('✗ Missing player-card container');
            }
            
            // Check for name heading (h2 or h1)
            if (htmlCode.includes('<h2>') || htmlCode.includes('<h1>')) {
                console.log('✓ Found heading tag');
                updateCheckPoint('has_name_heading', true, updatedCheckPoints);
                completedCount++;
            } else {
                console.log('✗ Missing heading tag');
            }
            
            // Check for paragraph tags - need at least 2 for number and position
            const pTagMatches = htmlCode.match(/<p[^>]*>/g);
            const pTagCount = pTagMatches ? pTagMatches.length : 0;
            console.log('Found', pTagCount, 'paragraph tags');
            
            if (pTagCount >= 1) {
                console.log('✓ Found number paragraph');
                updateCheckPoint('has_number_paragraph', true, updatedCheckPoints);
                completedCount++;
            }
            
            if (pTagCount >= 2) {
                console.log('✓ Found position paragraph');
                updateCheckPoint('has_position_paragraph', true, updatedCheckPoints);
                completedCount++;
            }
            
            // Check for proper nesting - div contains h2 and p tags
            if (htmlCode.includes('</div>') && htmlCode.includes('<h2>') && pTagCount >= 2) {
                console.log('✓ Proper nesting detected');
                updateCheckPoint('proper_nesting', true, updatedCheckPoints);
                completedCount++;
            } else {
                console.log('✗ Improper nesting');
            }
        }
        else if (stepId === '7') {
            if (htmlCode.includes('border')) {
                updateCheckPoint('has_border', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('background') || htmlCode.includes('background-color')) {
                updateCheckPoint('has_background', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('padding')) {
                updateCheckPoint('has_padding', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('border-radius')) {
                updateCheckPoint('has_border_radius', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('width')) {
                updateCheckPoint('has_width', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '8') {
            if (htmlCode.includes('stat-bar') || htmlCode.includes('stat')) {
                updateCheckPoint('has_stat_containers', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('stat-fill') || htmlCode.includes('fill')) {
                updateCheckPoint('has_stat_fills', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('width:') && (htmlCode.includes('%') || htmlCode.includes('px'))) {
                updateCheckPoint('has_width_styles', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('background') || htmlCode.includes('color')) {
                updateCheckPoint('has_visual_styles', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '9') {
            if (htmlCode.includes('forward') || htmlCode.includes('midfielder') || htmlCode.includes('defender')) {
                updateCheckPoint('has_position_classes', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('.forward') || htmlCode.includes('forward')) {
                updateCheckPoint('has_forward_style', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('#') || htmlCode.includes('rgb') || htmlCode.includes('color:')) {
                updateCheckPoint('has_color_difference', true, updatedCheckPoints);
                updateCheckPoint('has_visual_harmony', true, updatedCheckPoints);
                completedCount += 2;
            }
        } else if (stepId === '10') {
            if (htmlCode.includes('captain') || htmlCode.includes('class="captain"')) {
                updateCheckPoint('has_captain_class', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('👑') || htmlCode.includes('badge')) {
                updateCheckPoint('has_captain_badge', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('.captain') || htmlCode.includes('captain {')) {
                updateCheckPoint('has_special_style', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('animation') || htmlCode.includes('@keyframes')) {
                updateCheckPoint('has_animation', true, updatedCheckPoints);
                updateCheckPoint('has_visual_impact', true, updatedCheckPoints);
                completedCount += 2;
            }
        } else if (stepId === '15') {
            if (htmlCode.includes('팀') || htmlCode.includes('드림팀')) {
                updateCheckPoint('has_team_info', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.length > 200) {
                updateCheckPoint('has_multiple_players', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('grid') || htmlCode.includes('flex')) {
                updateCheckPoint('has_layout', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('animation') || htmlCode.includes('gradient')) {
                updateCheckPoint('has_creativity', true, updatedCheckPoints);
                completedCount++;
            }
            if (htmlCode.includes('<') && htmlCode.includes('>')) {
                updateCheckPoint('uses_all_concepts', true, updatedCheckPoints);
                completedCount++;
            }
        }

        console.log('Final completed count:', completedCount);
        console.log('Updated checkpoints:', updatedCheckPoints.map(cp => ({ id: cp.id, completed: cp.completed })));
        
        setCheckPoints(updatedCheckPoints);
        showValidationFeedback(completedCount, updatedCheckPoints);
    };

    // Step-specific validation logic for Python steps
    const validateStepSpecific = (stepId, validationResult, output, updatedCheckPoints) => {
        let completedCount = 0;

        if (stepId === '2') {
            if (validationResult.name_exists && output.includes('이름:')) {
                updateCheckPoint('name_variable', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.number_exists && output.includes('번호:')) {
                updateCheckPoint('number_variable', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.is_captain_exists && output.includes('주장')) {
                updateCheckPoint('is_captain_variable', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '3') {
            if (validationResult.player_exists && validationResult.player_type === 'dict') {
                updateCheckPoint('dictionary_created', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.player_keys?.includes('name')) {
                updateCheckPoint('has_name', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.player_keys?.includes('number')) {
                updateCheckPoint('has_number', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.player_keys?.includes('position')) {
                updateCheckPoint('has_position', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.player_keys?.length >= 3) {
                updateCheckPoint('correct_format', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '4') {
            if (validationResult.player_exists && 
                validationResult.player_values?.stats &&
                typeof validationResult.player_values.stats === 'object') {
                updateCheckPoint('has_stats_dict', true, updatedCheckPoints);
                completedCount++;
                
                const stats = validationResult.player_values.stats;
                if (stats.speed !== undefined) {
                    updateCheckPoint('has_speed', true, updatedCheckPoints);
                    completedCount++;
                }
                if (stats.shooting !== undefined) {
                    updateCheckPoint('has_shooting', true, updatedCheckPoints);
                    completedCount++;
                }
                if (stats.passing !== undefined) {
                    updateCheckPoint('has_passing', true, updatedCheckPoints);
                    completedCount++;
                }
            }
        } else if (stepId === '5') {
            if (validationResult.is_captain_exists && validationResult.is_captain_type === 'bool') {
                updateCheckPoint('captain_status', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.is_injured_exists && validationResult.is_injured_type === 'bool') {
                updateCheckPoint('injury_status', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.can_play_exists && validationResult.can_play_type === 'bool') {
                updateCheckPoint('play_status', true, updatedCheckPoints);
                completedCount++;
            }
            if (validationResult.has_yellow_card_exists && validationResult.has_yellow_card_type === 'bool') {
                updateCheckPoint('card_status', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '11') {
            if (code.includes('if ') && code.includes('is_captain')) {
                updateCheckPoint('has_if_statement', true, updatedCheckPoints);
                updateCheckPoint('checks_captain', true, updatedCheckPoints);
                completedCount += 2;
            }
            if (output.includes('👑') || code.includes('👑')) {
                updateCheckPoint('conditional_output', true, updatedCheckPoints);
                completedCount++;
            }
            if (output.includes('<') && output.includes('>')) {
                updateCheckPoint('proper_html', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '12') {
            if (code.includes('elif') || (code.includes('if') && code.includes('else'))) {
                updateCheckPoint('has_if_elif_structure', true, updatedCheckPoints);
                completedCount++;
            }
            if (code.includes('position') && (code.includes('공격수') || code.includes('수비수'))) {
                updateCheckPoint('checks_all_positions', true, updatedCheckPoints);
                completedCount++;
            }
            if (output.includes('⚡') || output.includes('🎯') || output.includes('🛡️')) {
                updateCheckPoint('has_position_icons', true, updatedCheckPoints);
                completedCount++;
            }
            if (code.includes('elif') && code.includes('else')) {
                updateCheckPoint('proper_logic_flow', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '13') {
            console.log('Step 13 validation - output:', output);
            console.log('Step 13 validation - code snippet:', code.substring(0, 200));
            
            if (code.includes('/') || code.includes('sum')) {
                console.log('✓ Found average calculation');
                updateCheckPoint('calculates_average', true, updatedCheckPoints);
                completedCount++;
            }
            if (code.includes('>=') && (code.includes('90') || code.includes('80'))) {
                console.log('✓ Found grade conditions');
                updateCheckPoint('has_grade_conditions', true, updatedCheckPoints);
                completedCount++;
            }
            
            // 등급 표시 체크 - 더 포괄적으로 확인
            const gradeKeywords = ['S등급', 'A등급', 'B등급', 'C등급', 'S급', 'A급', 'B급', 'C급', 'grade', '등급'];
            const hasGradeDisplay = gradeKeywords.some(keyword => output.includes(keyword));
            console.log('Grade keywords check:', gradeKeywords.map(k => ({ keyword: k, found: output.includes(k) })));
            
            if (hasGradeDisplay) {
                console.log('✓ Found grade display');
                updateCheckPoint('displays_grade', true, updatedCheckPoints);
                completedCount++;
            } else {
                console.log('✗ No grade display found in output');
            }
            
            if (code.includes('/') && code.includes('3')) {
                console.log('✓ Found proper calculation');
                updateCheckPoint('proper_calculation', true, updatedCheckPoints);
                completedCount++;
            }
        } else if (stepId === '14') {
            if (code.includes('[') && code.includes(']') && code.includes('{')) {
                updateCheckPoint('has_player_list', true, updatedCheckPoints);
                completedCount++;
            }
            if (code.includes('for ') && code.includes(' in ')) {
                updateCheckPoint('uses_for_loop', true, updatedCheckPoints);
                completedCount++;
            }
            if (output.length > 100) {
                updateCheckPoint('generates_multiple_cards', true, updatedCheckPoints);
                updateCheckPoint('consistent_format', true, updatedCheckPoints);
                completedCount += 2;
            }
        }

        return completedCount;
    };

    // Helper function to update checkpoint
    const updateCheckPoint = (id, completed, checkPointsArray) => {
        const index = checkPointsArray.findIndex(cp => cp.id === id);
        if (index !== -1) {
            checkPointsArray[index].completed = completed;
        }
    };

    // Show validation feedback
    const showValidationFeedback = (completedCount, updatedCheckPoints) => {
        const totalRequired = updatedCheckPoints.filter(cp => cp.required).length;
        
        // Save checkpoint status
        const checkpointsKey = `course_${courseId}_step_${stepId}_checkpoints`;
        const checkpointStatus = {};
        updatedCheckPoints.forEach(cp => {
            checkpointStatus[cp.id] = cp.completed;
        });
        localStorage.setItem(checkpointsKey, JSON.stringify(checkpointStatus));
        
        if (completedCount === totalRequired) {
            showFeedback('success');
            setStepCompleted(true);
            
            // Save success state
            const progressKey = `course_${courseId}_step_${stepId}_completed`;
            const codeKey = `course_${courseId}_step_${stepId}_code`;
            localStorage.setItem(progressKey, 'true');
            localStorage.setItem(codeKey, code);
        } else if (completedCount > 0) {
            showFeedback('partial');
        } else {
            showFeedback('retry');
        }
    };

    // Show feedback message
    const showFeedback = (type) => {
        const feedback = stepData?.content?.ko?.feedback?.[type];
        if (!feedback) return;

        setOutput(prevOutput => {
            return `${prevOutput}\n\n=== ${feedback.message} ===\n${feedback.detail}`;
        });
    };

    // Show solution
    const showSolutionCode = () => {
        console.log('showSolutionCode called for step:', stepId);
        console.log('stepData:', stepData);
        console.log('editor data:', stepData?.editor);
        console.log('solution:', stepData?.editor?.solution);
        console.log('solution ko:', stepData?.editor?.solution?.ko);
        console.log('modelAnswer ko:', stepData?.editor?.modelAnswer?.ko);

        // Save current user code if not showing solution
        if (!showSolution) {
            localStorage.setItem(`course_${courseId}_step_${stepId}_user_code`, code);
        }

        // Show solution - check multiple possible locations
        let solution = null;

        // Try different paths to find the solution
        if (typeof stepData?.editor?.solution === 'string') {
            // If solution is directly a string (not common but possible)
            solution = stepData.editor.solution;
        } else if (stepData?.editor?.solution?.ko) {
            // If solution has ko property
            solution = stepData.editor.solution.ko;
        } else if (stepData?.editor?.modelAnswer?.ko) {
            // If modelAnswer has ko property
            solution = stepData.editor.modelAnswer.ko;
        }

        console.log('Solution to set:', solution);
        console.log('Current code state:', code);

        if (solution) {
            // Force re-render by updating state in sequence
            setShowSolution(true);
            setOutput('💡 정답 코드를 확인하세요. 실행 버튼을 눌러 결과를 확인해보세요!');

            // If Monaco Editor is available, set value directly
            if (monacoEditorRef.current) {
                console.log('Setting solution directly via Monaco Editor');
                monacoEditorRef.current.setValue(solution);
                setCode(solution); // Also update React state
            } else {
                // Fallback to React state update with retry mechanism
                console.log('Monaco Editor not available, using React state');
                const trySolution = (attempts = 0) => {
                    if (attempts > 10) {
                        console.error('Failed to set solution after 10 attempts');
                        return;
                    }
                    
                    setCode(solution);
                    
                    // Verify if it worked by checking after a delay
                    setTimeout(() => {
                        if (monacoEditorRef.current && monacoEditorRef.current.getValue() !== solution) {
                            console.log(`Attempt ${attempts + 1} failed, retrying...`);
                            trySolution(attempts + 1);
                        } else {
                            console.log('Solution set successfully');
                        }
                    }, 100);
                };
                
                trySolution();
            }
        } else {
            console.error('No solution found for step:', stepId);
        }
    };

    // Reset to initial state
    const resetCode = () => {
        setShowSolution(false);
        setCode(stepData?.editor?.starterCode?.ko || '');
        setOutput('');
        setCheckPoints(checkPoints.map(cp => ({ ...cp, completed: false })));
        setStepCompleted(false);
        setAttemptCount(0);
        
        // Clear localStorage
        const attemptKey = `course_${courseId}_step_${stepId}_attempts`;
        const progressKey = `course_${courseId}_step_${stepId}_completed`;
        const codeKey = `course_${courseId}_step_${stepId}_code`;
        const checkpointsKey = `course_${courseId}_step_${stepId}_checkpoints`;
        localStorage.setItem(attemptKey, '0');
        localStorage.removeItem(progressKey);
        localStorage.removeItem(codeKey);
        localStorage.removeItem(checkpointsKey);
        localStorage.removeItem(`course_${courseId}_step_${stepId}_user_code`);
    };

    // Show hint
    const showNextHint = () => {
        if (!showHint) {
            setShowHint(true);
            setHintIndex(0);
        } else if (hintIndex < stepData.content.ko.hints.length - 1) {
            setHintIndex(hintIndex + 1);
        }
    };

    // Navigation functions
    const goToPrevStep = () => {
        const prevStep = parseInt(stepId) - 1;
        if (prevStep >= 1) {
            navigate(`/courses/${courseId}/step/${prevStep}`);
        } else {
            navigate(`/courses/${courseId}`);
        }
    };

    const goToNextStep = () => {
        const nextStep = parseInt(stepId) + 1;
        if (nextStep <= 15) {
            navigate(`/courses/${courseId}/step/${nextStep}`);
        } else {
            navigate(`/courses/${courseId}/complete`);
        }
    };


    // Check if all checkpoints are completed
    const allCheckPointsCompleted = checkPoints.every(cp => !cp.required || cp.completed);

    if (loading) {
        return <div className="loading">학습 내용을 불러오는 중...</div>;
    }

    if (!stepData) {
        return <div className="error">스텝을 찾을 수 없습니다.</div>;
    }

    // Article step rendering
    if (stepData?.type === 'article') {
        return (
            <div className="course-template-container">
                <NavBar />
                
                <header className="course-template-header">
                    <div className="header-content">
                        <div className="breadcrumb">
                            <span>{courseData?.title?.ko || '축구 카드 만들기'}</span>
                            <span className="separator">›</span>
                            <span>{stepData.title}</span>
                        </div>
                        <div className="progress-info">
                            단계 {stepId} / {courseData?.steps?.length || 15}
                        </div>
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${(stepId / (courseData?.steps?.length || 15)) * 100}%` }}
                        />
                    </div>
                </header>

                <div className="article-container">
                    <div className="article-content" 
                         dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(stepData.content) }} />
                </div>

                <div className="course-template-nav">
                    <button
                        className="nav-button prev"
                        onClick={() => navigate(`/courses/${courseId}`)}
                    >
                        ← 코스 소개
                    </button>
                    <button
                        className="nav-button next"
                        onClick={goToNextStep}
                    >
                        학습 시작하기 →
                    </button>
                </div>
            </div>
        );
    }

    // Coding step rendering
    const stepType = getCurrentStepType();
    const stepLanguage = getCurrentStepLanguage();

    return (
        <div className="course-template-container">
            <NavBar />
            
            <header className="course-template-header">
                <div className="header-content">
                    <div className="breadcrumb">
                        <span>{courseData?.title?.ko || '축구 카드 만들기'}</span>
                        <span className="separator">›</span>
                        <span>{stepData?.content?.ko?.header?.title}</span>
                    </div>
                    <div className="progress-info">
                        단계 {stepId} / {courseData?.steps?.length || 15}
                    </div>
                </div>
                <div className="progress-bar">
                    <div
                        className="progress-fill"
                        style={{ width: `${(stepId / (courseData?.steps?.length || 15)) * 100}%` }}
                    />
                </div>
            </header>

            <div className="course-template-main">
                {/* Left Panel - Instructions */}
                <div className="instruction-panel">
                    <div className="instruction-header">
                        <h1>{stepData.content.ko.header.title}</h1>
                        <p className="subtitle">{stepData.content.ko.header.subtitle}</p>
                    </div>

                    <div className="instruction-content">
                        <div className="main-instruction">
                            <h3>📝 미션</h3>
                            <p>{stepData.content.ko.instruction.mainText}</p>
                            <p className="detail-text">{stepData.content.ko.instruction.detailText}</p>
                        </div>

                        <div className="tips-section">
                            <h3>💡 팁</h3>
                            <ul>
                                {stepData.content.ko.instruction.tips.map((tip, index) => (
                                    <li key={index}>{tip}</li>
                                ))}
                            </ul>
                        </div>

                        {/* Hints Section */}
                        {showHint && stepData.content.ko.hints && (
                            <div className="hints-section">
                                <h3>🔍 힌트</h3>
                                <div className="hint-box">
                                    {stepData.content.ko.hints[hintIndex]?.text}
                                </div>
                            </div>
                        )}

                        <button
                            className="hint-button"
                            onClick={showNextHint}
                            disabled={showHint && hintIndex >= (stepData.content.ko.hints?.length - 1 || 0)}
                        >
                            {!showHint ? '힌트 보기' :
                                hintIndex < (stepData.content.ko.hints?.length - 1 || 0) ? '다음 힌트' : '모든 힌트를 확인했어요'}
                        </button>
                    </div>
                </div>

                {/* Right Panel - Coding */}
                <div className="coding-panel">
                    {/* Code Editor */}
                    <div className="editor-section">
                        <div className="editor-header">
                            <span className="editor-title">
                                {stepType === 'coding-html' ? 'HTML/CSS 코드 에디터' : 'Python 코드 에디터'}
                            </span>
                            <div className="editor-actions">
                                <button className="reset-button" onClick={resetCode}>
                                    초기화
                                </button>
                                <button
                                    className="run-button"
                                    onClick={runCode}
                                    disabled={isRunning}
                                >
                                    {isRunning ? '실행 중...' : '실행 ▶'}
                                </button>
                                <button 
                                    className="solution-button" 
                                    onClick={showSolutionCode}
                                >
                                    📝 정답 보기
                                </button>
                            </div>
                        </div>
                        
                        <MonacoEditor
                            value={code}
                            onChange={setCode}
                            language={stepLanguage}
                            theme="vs-light"
                            height="320px"
                            onEditorReady={(editor) => {
                                monacoEditorRef.current = editor;
                                console.log('Monaco Editor ready');
                            }}
                        />
                    </div>

                    {/* Output Section */}
                    <div className="output-section">
                        <div className="output-header">
                            {stepType === 'coding-html' ? 'HTML 미리보기' : '실행 결과'}
                        </div>
                        <div className="output-content">
                            {stepType === 'coding-html' ? (
                                <div className="html-output">
                                    <iframe
                                        ref={iframeRef}
                                        className="preview-iframe"
                                        title="HTML Preview"
                                        style={{ width: '100%', height: '200px', border: '1px solid #ddd' }}
                                    />
                                    {output && (
                                        <div className="output-info" dangerouslySetInnerHTML={{ __html: output }} />
                                    )}
                                </div>
                            ) : (
                                <pre className="python-output">
                                    {output || '코드를 실행하면 결과가 여기에 표시됩니다.'}
                                </pre>
                            )}
                        </div>
                    </div>

                    {/* CheckPoints */}
                    <div className="checkpoints-section">
                        <h3>✅ 체크포인트</h3>
                        <div className="checkpoints-list">
                            {checkPoints.map(cp => (
                                <div
                                    key={cp.id}
                                    className={`checkpoint-item ${cp.completed ? 'completed' : ''}`}
                                >
                                    <div className="checkpoint-icon">
                                        {cp.completed ? '✓' : '○'}
                                    </div>
                                    <div className="checkpoint-info">
                                        <div className="checkpoint-label">{cp.label}</div>
                                        <div className="checkpoint-desc">{cp.description}</div>
                                    </div>
                                    <div className="checkpoint-points">
                                        {cp.points}점
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="course-template-nav">
                <button
                    className="nav-button prev"
                    onClick={goToPrevStep}
                    disabled={stepId === '1'}
                >
                    ← 이전
                </button>

                <button
                    className="nav-button next"
                    onClick={goToNextStep}
                    disabled={!allCheckPointsCompleted && !stepCompleted}
                >
                    {(allCheckPointsCompleted || stepCompleted) ? 
                        '다음 미션으로 →' : 
                        '미션을 완료해주세요'}
                </button>
            </div>
        </div>
    );
};

export default CourseTemplate;